import { ChangeEvent, ReactElement, useContext, useState } from 'react';
import { Button, InputAdornment, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { Close, ExpandMoreRounded, SearchRounded } from '@mui/icons-material';
import { useField } from 'formik';
import { get, uniq } from 'lodash';

// Components
import { InputOption } from './InputOption';

// Styles
import styles from './MultiTypeahead.module.scss';

import { IDisabledContext, IDisabledState } from 'core';
import { DisabledContext } from '../forms/DisabledContext';

// Types
export interface ILabelType {
  description?: string;
  id: string;
  label: string;
  name?: string;
}

const StyledAutocomplete = styled(Autocomplete)({
  '.MuiAutocomplete-listbox': {
    borderRadius: 0,
    boxShadow: 'none',
    padding: 0,
  },
  '.MuiAutocomplete-option': {
    backgroundColor: '#fff',
    borderBottom: '0.03125rem solid #dedede',
    borderRadius: 0,
    padding: '0.5rem 1rem',
    '&[aria-selected="true"]': {
      backgroundColor: '#fff',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
    '&:last-child': {
      borderBottom: 0,
    },
  },
  '.MuiAutocomplete-paper': {
    borderRadius: 0,
    boxShadow: 'none',
    margin: 0,
    padding: 0,
  },
  '.MuiAutocomplete-popperDisablePortal': {
    backgroundColor: '#fff',
    border: '0.03125rem solid #dedede',
    position: 'relative',
  },
});

interface IProps {
  onInputChange?(_: ChangeEvent<HTMLInputElement>, val: string): void;
  options: ILabelType[];
  placeholder: string;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  id: string;
  initialOptions?: ILabelType[];
  fullWidth?: boolean;
}

export const MultiTypeahead = (props: IProps): ReactElement => {
  const { onInputChange, options, placeholder, setFieldValue, id, initialOptions, fullWidth } = props;
  const disabledContextShape: IDisabledContext = useContext<IDisabledContext>(DisabledContext);
  const disabledState: IDisabledState = get(disabledContextShape, id) as IDisabledState;
  const [field] = useField<string[] | { id: string }[]>(id);
  const { value } = field;
  const initialAutocompleteValues =
    initialOptions ||
    options?.filter(
      ({ id }) =>
        (value as string[])?.includes(id) ||
        (value as { id: string }[])?.find((val: { id: string }): boolean => val.id === id),
    );
  const [values, setValues] = useState<ILabelType[]>(initialAutocompleteValues || []);
  const [pendingValues, setPendingValues] = useState<ILabelType[]>(initialAutocompleteValues || []);

  // Select the dropdown value.
  const onClickDropdownOption = (_event: ChangeEvent<never>, newValue: ILabelType[]): void => {
    setPendingValues(newValue);
    setValues(newValue);
    const sortedSelectedValues: string[] = uniq(
      newValue
        ?.sort((labelA: ILabelType, labelB: ILabelType): number => {
          if (labelA?.label > labelB?.label) {
            return 1;
          }
          if (labelA?.label < labelB?.label) {
            return -1;
          }
          return 0;
        })
        ?.map(({ id }: ILabelType): string => id),
    );
    if (setFieldValue) setFieldValue(id, sortedSelectedValues, true);
  };

  // Select (remove) from outside the dropdown.
  const onClickOutsideSelected = (newValue: ILabelType): void => {
    const filteredPendingValues = pendingValues.filter((value) => value.id !== newValue.id);
    const filteredBaseValues = values.filter((value) => value.id !== newValue.id);
    const fieldValues = filteredBaseValues.map(({ id: Id }) => Id);
    setPendingValues(filteredPendingValues);
    setValues(filteredBaseValues);
    setFieldValue(id, fieldValues, true);
  };

  const renderField = (params: AutocompleteRenderInputParams): ReactElement => {
    params.InputProps.startAdornment = (
      <InputAdornment position="start">
        <SearchRounded />
      </InputAdornment>
    );
    params.InputProps.endAdornment = (
      <InputAdornment position="end">
        <ExpandMoreRounded />
      </InputAdornment>
    );
    return (
      <TextField
        {...params}
        placeholder={placeholder}
        variant="outlined"
        slotProps={{ htmlInput: { ...params.inputProps, 'aria-label': placeholder } }}
      />
    );
  };

  const renderOption = (props, option: ILabelType, { selected }): ReactElement => {
    return (
      <li {...props}>
        <InputOption option={option} selected={selected} />
      </li>
    );
  };

  const optionLabel = ({ label }: ILabelType): string => label;

  // Display the selected options at the top of the options.
  const sortedOptions: ILabelType[] = [...options].sort((a: ILabelType, b: ILabelType): number => {
    let ai = values.indexOf(a);
    ai = ai === -1 ? values.length + options.indexOf(a) : ai;
    let bi = values.indexOf(b);
    bi = bi === -1 ? values.length + options.indexOf(b) : bi;
    return ai - bi;
  });

  return (
    <div className={styles['multi-autocomplete']}>
      <StyledAutocomplete
        sx={{ ...(fullWidth && { width: '100%' }) }}
        aria-label={placeholder}
        disableCloseOnSelect
        disablePortal
        getOptionDisabled={(option: ILabelType) => {
          return disabledState?.isDisabled && disabledState?.disabledOptions?.includes(option.id);
        }}
        getOptionLabel={optionLabel}
        isOptionEqualToValue={(option: ILabelType, value: ILabelType) => option?.id === value?.id}
        multiple
        noOptionsText="No options"
        onInputChange={onInputChange}
        onChange={onClickDropdownOption}
        openOnFocus
        options={sortedOptions}
        renderInput={renderField}
        renderOption={renderOption}
        value={pendingValues}
      />

      {/* List of selected items. */}
      {values.length >= 1 && (
        <div className={styles['selected-summary']}>
          {values.map((item: ILabelType): ReactElement => {
            const { id, label } = item;

            return (
              <div className={styles['selected-summary-item']} key={id}>
                <div>{label}</div>
                <Button
                  onClick={(): void => onClickOutsideSelected(item)}
                  title="remove"
                  disabled={disabledState?.isDisabled && disabledState?.disabledOptions?.includes(id)}
                >
                  <Close />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
